import { IDisplayableAvatarImageModel } from "@next-platform/packages-ts-types";
import { Icon, IconSize, IconVariants, Image, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback } from "react";

import { useScrollIntoView } from "@/components/AvatarGeneratorModal/hooks/useScrollIntoView";
import { type IModelSelectorProps, ModelTag } from "@/components/AvatarGeneratorModal/types";
import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils";

const MODEL_PREVIEW_SIZE = 90;

const MODEL_PREVIEW_MIN_WIDTH_STYLE = { minWidth: MODEL_PREVIEW_SIZE } as const;
const MODEL_PREVIEW_PLACEHOLDER_STYLE = { width: MODEL_PREVIEW_SIZE, height: MODEL_PREVIEW_SIZE } as const;

// Known tag strings that have translated badge labels. Unknown tag values render as the raw string.

const renderModalTag = (tag: ModelTag) => {
  const tags = {
    [ModelTag.NEW]: <Trans i18nKey="common:modal.generateAvatar.modelTag.new" />,
    [ModelTag.POPULAR]: <Trans i18nKey="common:modal.generateAvatar.modelTag.popular" />,
    [ModelTag.BETA]: <Trans i18nKey="common:modal.generateAvatar.modelTag.beta" />,
  };
  return tags[tag] ?? tag;
};

const ModelPreview = ({ model, isSelected }: { model: IDisplayableAvatarImageModel; isSelected: boolean }) => {
  const previewClasses = cn(
    "rounded-md outline outline-offset-2",
    isSelected ? "outline-blue-500" : "outline-transparent",
    model.isDisabled ? "opacity-50" : "",
  );

  return (
    <div className="relative" style={MODEL_PREVIEW_MIN_WIDTH_STYLE}>
      {model.imageUrl ? (
        <Image
          src={model.imageUrl}
          alt={model.name}
          width={MODEL_PREVIEW_SIZE}
          height={MODEL_PREVIEW_SIZE}
          className={cn("object-cover", previewClasses)}
          style={MODEL_PREVIEW_MIN_WIDTH_STYLE}
        />
      ) : (
        <div className={cn("flex items-center justify-center bg-gray-7", previewClasses, "mb-0.5")} style={MODEL_PREVIEW_PLACEHOLDER_STYLE}>
          <Icon icon={IconVariants.SPARKLES} size={IconSize.LARGE} />
        </div>
      )}
      {model.tag ? (
        <div className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
          {renderModalTag(model.tag as ModelTag)}
        </div>
      ) : null}
    </div>
  );
};

interface IModelItemProps {
  model: IDisplayableAvatarImageModel;
  isSelected: boolean;
  registerRef: (id: string, el: HTMLDivElement | null) => void;
  onModelChange: (id: string) => void;
  onDisabledModelClick?: () => void;
}

const ModelItem = ({ model, isSelected, registerRef, onModelChange, onDisabledModelClick }: IModelItemProps) => {
  const assignRef = useCallback((el: HTMLDivElement | null) => registerRef(model.id, el), [registerRef, model.id]);
  const handleClick = useCallback(() => {
    if (model.isDisabled) {
      onDisabledModelClick?.();
      return;
    }
    onModelChange(model.id);
  }, [model.isDisabled, model.id, onModelChange, onDisabledModelClick]);

  return (
    <div
      ref={assignRef}
      className={cn("flex flex-col items-center gap-1", model.isDisabled ? "cursor-not-allowed" : "cursor-pointer")}
      onClick={handleClick}
    >
      <ModelPreview model={model} isSelected={isSelected} />
      <span className="text-[14px] text-center grid">
        <span className="font-semibold invisible col-start-1 row-start-1">{model.name}</span>
        <span className={cn("col-start-1 row-start-1", isSelected ? "font-semibold" : "font-light")}>{model.name}</span>
      </span>
    </div>
  );
};

export const ModelSelector = ({ imageModels, modelStyle, onModelChange, onDisabledModelClick }: IModelSelectorProps) => {
  const modelRefs = useScrollIntoView<HTMLDivElement>(modelStyle);
  const registerRef = useCallback(
    (id: string, el: HTMLDivElement | null) => {
      modelRefs.current[id] = el;
    },
    [modelRefs],
  );

  return (
    <Col>
      <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="ml-[2px]">
        <Trans i18nKey="common:modal.generateAvatar.chooseModel.label" />
      </Text>
      <Row className="w-full gap-2 overflow-x-auto p-1 scrollbar-hide">
        {imageModels?.map((model: IDisplayableAvatarImageModel) => (
          <ModelItem
            key={model.id}
            model={model}
            isSelected={modelStyle === model.id}
            registerRef={registerRef}
            onModelChange={onModelChange}
            onDisabledModelClick={onDisabledModelClick}
          />
        ))}
      </Row>
    </Col>
  );
};
